import React, { useState } from 'react';
import { Button, Card, Row, Col, Space, message, Tag, Typography, Popconfirm } from 'antd';
import { SaveOutlined, DeleteOutlined, PlusOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import FormCol from './FormCol';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

export default function Section({ id, name, saveFn, deleteFn, initialRows }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(initialRows || []);
  const [isSaved, setIsSaved] = useState(!!initialRows);

  const addRow = () => {
    setRows([...rows, { id: uuidv4(), cols: [] }]);
    setIsSaved(false);
  };

  const deleteRow = (rowId) => {
    setRows(rows.filter(r => r.id !== rowId));
    setIsSaved(false);
  };

  const addCol = (rowId) => {
    setRows(rows.map(row => row.id === rowId ? {
      ...row,
      cols: [...row.cols, { id: uuidv4(), width: '1', field: { id: uuidv4(), name: '', type: '' } }]
    } : row));
    setIsSaved(false);
  };

  const deleteCol = (rowId, colId) => {
    setRows(rows.map(row => row.id === rowId ? {
      ...row,
      cols: row.cols.filter(col => col.id !== colId)
    } : row));
    setIsSaved(false);
  };

  const updateCol = (rowId, colId, updatedCol) => {
    setRows(rows.map(row => row.id === rowId ? {
      ...row,
      cols: row.cols.map(col => col.id === colId ? updatedCol : col)
    } : row));
    setIsSaved(false);
  };

  const handleSave = () => {
    const hasContent = rows.length > 0 && rows.every(r => r.cols.length > 0);
    const allValid = rows.every(r => r.cols.every(c => {
      const fieldName = c.field.name;
      const hasName = typeof fieldName === 'object'
        ? (fieldName.en?.trim() || fieldName.ar?.trim())
        : (fieldName && fieldName.trim());
      return hasName && c.field.type;
    }));

    if (!hasContent || !allValid) {
      message.error(t('section.pleaseAddFields'));
      return;
    }

    saveFn(id, { id, title: name, rows });
    setIsSaved(true);
    message.success(t('section.configurationCached'));
  };

  const displayName = typeof name === 'object'
    ? (name.en || name.ar || t('section.untitled'))
    : name;

  const cardTitle = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <Text strong>{t('section.sectionTitle', { name: displayName })}</Text>
      {isSaved ? (
        <Tag icon={<CheckCircleOutlined />} color="success" bordered={false} style={{ background: '#f6ffed', color: '#52c41a' }}>{t('section.saved')}</Tag>
      ) : (
        <Tag icon={<ClockCircleOutlined />} color="default" bordered={false} style={{ background: '#f5f5f5' }}>{t('section.unsavedChanges')}</Tag>
      )}
    </div>
  );

  return (
    <Card
      title={cardTitle}
      style={{ marginBottom: 24, borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
      extra={
        <Space>
          <Popconfirm title={t('section.deleteEntireSection')} onConfirm={() => deleteFn(id)} okText={t('common.yes')} cancelText={t('common.no')}>
            <Button icon={<DeleteOutlined />}>{t('section.deleteSection')}</Button>
          </Popconfirm>
          <Button type={isSaved ? "default" : "primary"} icon={<SaveOutlined />} onClick={handleSave}>
            {isSaved ? t('section.update') : t('section.saveSection')}
          </Button>
        </Space>
      }
    >
      {rows.map((row) => (
        <Card
          title={t('section.row')}
          key={row?.id}
          style={{ marginBottom: 16 }}
          extra={
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => deleteRow(row.id)}
            >
              {t('section.removeRow')}
            </Button>
          }
        >
          <Row gutter={[16, 16]} style={{ marginTop: 10 }}>
            {row.cols.map((col) => (
              <Col span={8} key={col.id}>
                <FormCol
                  colData={col}
                  onUpdate={(data) => updateCol(row.id, col.id, data)}
                  onDelete={() => deleteCol(row.id, col.id)}
                />
              </Col>
            ))}
            <Col span={4} style={{ display: 'flex', alignItems: 'center' }}>
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => addCol(row.id)} block>{t('section.addCol')}</Button>
            </Col>
          </Row>

        </Card>
      ))}
      <Button type="primary" ghost icon={<PlusOutlined />} onClick={addRow} block>{t('section.addRow')}</Button>
    </Card>
  );
}